import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Button,
  Tooltip,
  Chip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import {
  fetchNewsletterById,
  updateNewsletter,
  uploadNewsletterCoverImage,
  uploadNewsletterPdf,
} from '../controllers/newsletterController';
import { resolveNewsletterAssetUrl } from '../api/httpClientNewsletter';

const EditNewsletterDialog = ({ open, newsletterId, onClose, onSuccess }) => {
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [publishedAtUtcValue, setPublishedAtUtcValue] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    if (!open || !newsletterId) {
      setEditData(null);
      setEditError('');
      setTitleValue('');
      setPublishedAtUtcValue('');
      setIsEditingTitle(false);
      setUploadingPdf(false);
      return;
    }

    let active = true;
    setEditLoading(true);
    setEditError('');
    (async () => {
      try {
        const data = await fetchNewsletterById(newsletterId);
        if (!active) return;
        setEditData(data);
        setTitleValue(data.title);
        if (data.publishedAtUtc) {
          const date = new Date(data.publishedAtUtc);
          const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          setPublishedAtUtcValue(localDateTime);
        } else {
          setPublishedAtUtcValue('');
        }
        setIsEditingTitle(false);
      } catch (e) {
        if (!active) return;
        setEditError(e?.message || 'Bülten alınamadı');
      } finally {
        if (active) setEditLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, newsletterId]);

  const handleClose = () => {
    setEditData(null);
    setEditError('');
    setTitleValue('');
    setPublishedAtUtcValue('');
    setIsEditingTitle(false);
    setUploadingPdf(false);
    onClose();
  };

  const handleUpdate = async (updates) => {
    if (!editData) return;
    setUpdating(true);
    try {
      const updatesWithDate = { ...updates };
      if (updates.publishedAtUtc !== undefined) {
        updatesWithDate.publishedAtUtc = updates.publishedAtUtc ? new Date(`${updates.publishedAtUtc}:00`).toISOString() : null;
      }
      const updated = await updateNewsletter({ ...editData, ...updatesWithDate });
      setEditData(updated);
      if (updated.publishedAtUtc) {
        const date = new Date(updated.publishedAtUtc);
        const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setPublishedAtUtcValue(localDateTime);
      } else {
        setPublishedAtUtcValue('');
      }
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('Güncelleme hatası:', e);
    } finally {
      setUpdating(false);
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
  };

  const handleTitleSave = async () => {
    if (!editData || titleValue.trim() === '') return;
    setUpdating(true);
    try {
      const updated = await updateNewsletter({
        ...editData,
        title: titleValue.trim(),
        publishedAtUtc: editData.publishedAtUtc || null,
      });
      setEditData(updated);
      setIsEditingTitle(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('Başlık güncellenemedi:', e);
    } finally {
      setUpdating(false);
    }
  };

  const handleTitleCancel = () => {
    if (editData) {
      setTitleValue(editData.title);
    }
    setIsEditingTitle(false);
  };

  const handleCoverImageUpload = async (file) => {
    if (!editData) return;
    try {
      const result = await uploadNewsletterCoverImage({ newsletterId: editData.id, file });
      setEditData((prev) => ({ ...prev, coverImageUrl: result }));
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('Kapak görseli yüklenemedi:', e);
    }
  };

  const handlePdfUpload = async (file) => {
    if (!editData) return;
    setUploadingPdf(true);
    setUpdating(true);
    try {
      const result = await uploadNewsletterPdf({ newsletterId: editData.id, file });
      setEditData((prev) => ({ ...prev, pdfUrl: result }));
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('PDF yüklenemedi:', e);
    } finally {
      setUploadingPdf(false);
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    const timePart = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} - ${timePart}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Bülten Düzenle
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 3, px: 3 }}>
        {editLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editError ? (
          <Typography color="error" sx={{ py: 2 }}>
            {editError}
          </Typography>
        ) : editData ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, alignItems: 'flex-start' }}>
            {/* Sol Taraf - Kapak Görseli */}
            <Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '3/2',
                  bgcolor: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px dashed #e6e6e6',
                }}
              >
                {/* Düzenleme Butonu */}
                <IconButton
                  component="label"
                  disabled={updating}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: '#E7EDF4',
                    borderRadius: '6px',
                    width: 32,
                    height: 32,
                    zIndex: 1,
                    '&:hover': {
                      backgroundColor: '#CFDBEA',
                    },
                    '& svg': {
                      fontSize: '16px',
                      color: '#0E4D94',
                    },
                  }}
                >
                  <EditIcon />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCoverImageUpload(file);
                      e.target.value = '';
                    }}
                  />
                </IconButton>

                {editData.coverImageUrl ? (
                  <Box
                    component="img"
                    src={resolveNewsletterAssetUrl(editData.coverImageUrl)}
                    alt={editData.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : (
                  <Stack spacing={1} alignItems="center">
                    <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#0E4D94', fontWeight: 500 }}>
                      600 × 400
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Sağ Taraf - İçerik Bilgileri ve Ayarlar */}
            <Box>
              <Stack spacing={3}>
                {/* Üst Bilgiler */}
                <Stack spacing={2.5}>
                  {/* Tarih ve Status */}
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                      {formatDate(editData.createdAtUtc)}
                    </Typography>
                    {editData.isActive ? (
                      <Chip size="small" label="Aktif" sx={{ backgroundColor: '#DFFFEA', color: '#17C653', fontWeight: 600, fontSize: '12px', borderRadius: '12px' }} />
                    ) : (
                      <Chip size="small" label="Pasif" sx={{ backgroundColor: '#FFEEF3', color: '#F8285A', fontWeight: 600, fontSize: '12px', borderRadius: '12px' }} />
                    )}
                    {editData.isPublished && (
                      <Chip size="small" label="Yayınlandı" sx={{ backgroundColor: '#DFFFEA', color: '#17C653', fontWeight: 600, fontSize: '12px', borderRadius: '12px' }} />
                    )}
                  </Stack>

                  {/* Başlık */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isEditingTitle ? (
                      <>
                        <TextField
                          value={titleValue}
                          onChange={(e) => setTitleValue(e.target.value)}
                          fullWidth
                          disabled={updating}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '5px',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0E4D94',
                              },
                            },
                            '& .MuiInputBase-input': {
                              py: 1.2,
                              px: 1.5,
                              fontSize: '14px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e6e6e6',
                            },
                          }}
                        />
                        <Tooltip title="Kaydet">
                          <IconButton
                            size="small"
                            onClick={handleTitleSave}
                            disabled={updating || titleValue.trim() === ''}
                            sx={{
                              backgroundColor: '#DFFFEA',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: '#C8F5D8',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#17C653',
                              },
                              '&:disabled': {
                                backgroundColor: '#f5f5f5',
                                color: '#a3a3a3',
                              },
                            }}
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="İptal">
                          <IconButton
                            size="small"
                            onClick={handleTitleCancel}
                            disabled={updating}
                            sx={{
                              backgroundColor: '#FFEEF3',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#F8285A',
                              },
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '24px', color: '#444444', flexGrow: 1 }}>
                          {editData.title}
                        </Typography>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={handleTitleEdit}
                            disabled={updating}
                            sx={{
                              backgroundColor: '#E7EDF4',
                              borderRadius: '6px',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: '#CFDBEA',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#0E4D94',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>

                  {/* PDF Dosyası */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      PDF Dosyası
                    </Typography>
                    {uploadingPdf && (
                      <Alert 
                        severity="info" 
                        sx={{ mb: 1.5, fontSize: '13px' }}
                        icon={<CircularProgress size={18} sx={{ color: '#0E4D94' }} />}
                      >
                        Yüklediğiniz belgenin boyutu sıkıştırılıyor. Lütfen sayfayı kapatmadan bekleyiniz
                      </Alert>
                    )}
                    {editData.pdfUrl ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                        {/* Mevcut PDF Dosyası */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: '#f5f5f5',
                            borderRadius: '5px',
                            border: '1px solid #e6e6e6',
                            maxWidth: '250px',
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#F8285A' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: '#444444',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            {editData.pdfUrl.split('/').pop() || 'PDF Dosyası'}
                          </Typography>
                          <IconButton
                            size="small"
                            component="a"
                            href={resolveNewsletterAssetUrl(editData.pdfUrl)}
                            download={editData.pdfUrl.split('/').pop() || 'newsletter.pdf'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: 'transparent',
                              borderRadius: '4px',
                              width: 20,
                              height: 20,
                              minWidth: 20,
                              padding: 0,
                              '&:hover': {
                                backgroundColor: '#E7EDF4',
                              },
                              '& svg': {
                                fontSize: '14px',
                                color: '#0E4D94',
                              },
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                        {/* PDF Değiştir Butonu */}
                        <Box
                          component="label"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: '#ffffff',
                            borderRadius: '5px',
                            border: '1px dashed #e6e6e6',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#0E4D94',
                              backgroundColor: '#f0f0f0',
                            },
                          }}
                        >
                          <input
                            type="file"
                            accept=".pdf"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handlePdfUpload(file);
                              e.target.value = '';
                            }}
                            disabled={updating}
                          />
                          <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: '#444444',
                              fontWeight: 500,
                            }}
                          >
                            Değiştir
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        component="label"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: '#f5f5f5',
                          borderRadius: '5px',
                          border: '1px solid #e6e6e6',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          width: 'fit-content',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            backgroundColor: '#f0f0f0',
                          },
                        }}
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handlePdfUpload(file);
                            e.target.value = '';
                          }}
                          disabled={updating}
                        />
                        <PictureAsPdfIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#444444',
                            fontWeight: 500,
                          }}
                        >
                          PDF Dosyası Ekle
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Durum */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Durum
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editData.isActive}
                          onChange={(e) => handleUpdate({ isActive: e.target.checked })}
                          disabled={updating}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#0E4D94',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#0E4D94',
                            },
                          }}
                        />
                      }
                      label={editData.isActive ? 'Aktif' : 'Pasif'}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13px',
                          ml: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* Yayınlandı */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Yayınlandı
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editData.isPublished || false}
                          onChange={(e) => handleUpdate({ isPublished: e.target.checked })}
                          disabled={updating}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#0E4D94',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#0E4D94',
                            },
                          }}
                        />
                      }
                      label={editData.isPublished ? 'Yayınlandı' : 'Yayınlanmadı'}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '13px',
                          ml: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* Yayınlanma Tarihi */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Yayınlanma Tarihi
                    </Typography>
                    <TextField
                      type="datetime-local"
                      fullWidth
                      value={publishedAtUtcValue}
                      onChange={(e) => {
                        setPublishedAtUtcValue(e.target.value);
                        handleUpdate({ publishedAtUtc: e.target.value || null });
                      }}
                      InputLabelProps={{ shrink: true }}
                      disabled={updating}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '5px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default EditNewsletterDialog;
